import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Badge,
} from '@mui/material';
import {
  Visibility,
  Email,
  Phone,
  Refresh,
  Search,
  FilterList,
  Person,
  Business,
  Description,
  CalendarToday,
  CheckCircle,
  Error,
  Schedule,
  DocumentScanner,
} from '@mui/icons-material';
import { adminApi } from '../services/api.ts';

interface Cliente {
  id: string;
  nombre: string;
  email: string;
  telefono: string;
  tipoDocumento: string;
  numeroDocumento: string;
  esEmpresa: boolean;
  fechaRegistro: string;
  documentosAsociados: number;
  estado: 'ACTIVO' | 'INACTIVO';
}

interface DocumentoCliente {
  id: string;
  titulo: string;
  estado: string;
  firmante: string;
  email: string;
  fechaCreacion: string;
  fechaExpiracion: string;
}

const ListadoClientes: React.FC = () => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<string>('TODOS');
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [documentosCliente, setDocumentosCliente] = useState<DocumentoCliente[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documentosDialogOpen, setDocumentosDialogOpen] = useState(false);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);

  const cargarClientes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.listarClientes();
      if (response.data && response.data.success) {
        setClientes(response.data.data || []);
      } else {
        setError(response.data?.message || 'Error al cargar clientes');
      }
    } catch (err: any) {
      console.error('Error loading clients:', err);
      if (err.code === 'ERR_NETWORK') {
        setError('Error de conexión. Verifica que el servidor esté funcionando.');
      } else {
        setError(err.response?.data?.message || err.message || 'Error al cargar clientes');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarClientes();
  }, []);

  const handleVerDetalle = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setDialogOpen(true);
  };

  const handleVerDocumentos = async (cliente: Cliente) => {
    try {
      setLoadingDocumentos(true);
      const response = await adminApi.obtenerDocumentosPorCliente(cliente.id);
      if (response.data && response.data.success) {
        setDocumentosCliente(response.data.data || []);
        setSelectedCliente(cliente);
        setDocumentosDialogOpen(true);
      } else {
        alert('Error al cargar documentos del cliente');
      }
    } catch (error: any) {
      console.error('Error al cargar documentos del cliente:', error);
      if (error.code === 'ERR_NETWORK') {
        alert('Error de conexión. Verifica que el servidor esté funcionando.');
      } else {
        alert(error.response?.data?.message || error.message || 'Error al cargar documentos del cliente');
      }
    } finally {
      setLoadingDocumentos(false);
    }
  };

  const handleEnviarEmail = async (cliente: Cliente) => {
    try {
      // Aquí implementarías el envío de email
      alert(`Funcionalidad de envío de email a ${cliente.email} en desarrollo`);
    } catch (error) {
      console.error('Error al enviar email:', error);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado.toUpperCase()) {
      case 'ACTIVO':
        return 'success';
      case 'INACTIVO':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEstadoIcon = (estado: string) => {
    switch (estado.toUpperCase()) {
      case 'ACTIVO':
        return <CheckCircle />;
      case 'INACTIVO':
        return <Error />;
      default:
        return <Schedule />;
    }
  };

  const getDocumentoEstadoColor = (estado: string) => {
    switch (estado.toUpperCase()) {
      case 'FIRMADO':
        return 'success';
      case 'PENDIENTE':
        return 'warning';
      case 'EXPIRADO':
        return 'error';
      case 'SUBIDO':
        return 'info';
      default:
        return 'default';
    }
  };

  const clientesFiltrados = clientes.filter(cliente => {
    const matchesSearch = cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cliente.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cliente.numeroDocumento.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterTipo === 'TODOS' || 
                         (filterTipo === 'EMPRESAS' && cliente.esEmpresa) ||
                         (filterTipo === 'PERSONAS' && !cliente.esEmpresa);
    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Listado de Clientes
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtros y búsqueda */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por nombre, email o documento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              variant="outlined"
              label="Filtrar por tipo"
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              InputProps={{
                startAdornment: <FilterList sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            >
              <option value="TODOS">Todos los tipos</option>
              <option value="PERSONAS">Personas</option>
              <option value="EMPRESAS">Empresas</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={cargarClientes}
            >
              Actualizar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Estadísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Clientes
              </Typography>
              <Typography variant="h4">
                {clientes.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Personas
              </Typography>
              <Typography variant="h4" color="primary.main">
                {clientes.filter(c => !c.esEmpresa).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Empresas
              </Typography>
              <Typography variant="h4" color="secondary.main">
                {clientes.filter(c => c.esEmpresa).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Activos
              </Typography>
              <Typography variant="h4" color="success.main">
                {clientes.filter(c => c.estado === 'ACTIVO').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla de clientes */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Cliente</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Teléfono</strong></TableCell>
              <TableCell><strong>Documento</strong></TableCell>
              <TableCell><strong>Tipo</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell><strong>Documentos</strong></TableCell>
              <TableCell><strong>Fecha Registro</strong></TableCell>
              <TableCell><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientesFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No se encontraron clientes
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              clientesFiltrados.map((cliente) => (
                <TableRow key={cliente.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, bgcolor: cliente.esEmpresa ? 'secondary.main' : 'primary.main' }}>
                        {cliente.esEmpresa ? <Business /> : <Person />}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {cliente.nombre}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {cliente.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Email sx={{ mr: 1, fontSize: 16 }} />
                      {cliente.email}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Phone sx={{ mr: 1, fontSize: 16 }} />
                      {cliente.telefono}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {cliente.tipoDocumento}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {cliente.numeroDocumento}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={cliente.esEmpresa ? <Business /> : <Person />}
                      label={cliente.esEmpresa ? 'Empresa' : 'Persona'}
                      color={cliente.esEmpresa ? 'secondary' : 'primary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getEstadoIcon(cliente.estado)}
                      label={cliente.estado}
                      color={getEstadoColor(cliente.estado) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Badge badgeContent={cliente.documentosAsociados} color="primary">
                      <IconButton
                        size="small"
                        onClick={() => handleVerDocumentos(cliente)}
                        disabled={loadingDocumentos}
                      >
                        <DocumentScanner />
                      </IconButton>
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <CalendarToday sx={{ mr: 1, fontSize: 16 }} />
                      {formatDate(cliente.fechaRegistro)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleVerDetalle(cliente)}
                        title="Ver detalle"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEnviarEmail(cliente)}
                        title="Enviar email"
                      >
                        <Email />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de detalle del cliente */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalle del Cliente
        </DialogTitle>
        <DialogContent>
          {selectedCliente && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ mr: 2, width: 64, height: 64, bgcolor: selectedCliente.esEmpresa ? 'secondary.main' : 'primary.main' }}>
                      {selectedCliente.esEmpresa ? <Business /> : <Person />}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedCliente.nombre}
                      </Typography>
                      <Chip
                        icon={selectedCliente.esEmpresa ? <Business /> : <Person />}
                        label={selectedCliente.esEmpresa ? 'Empresa' : 'Persona'}
                        color={selectedCliente.esEmpresa ? 'secondary' : 'primary'}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {selectedCliente.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Teléfono
                  </Typography>
                  <Typography variant="body1">
                    {selectedCliente.telefono}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Tipo de Documento
                  </Typography>
                  <Typography variant="body1">
                    {selectedCliente.tipoDocumento}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Número de Documento
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {selectedCliente.numeroDocumento}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Estado
                  </Typography>
                  <Chip
                    icon={getEstadoIcon(selectedCliente.estado)}
                    label={selectedCliente.estado}
                    color={getEstadoColor(selectedCliente.estado) as any}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Documentos Asociados
                  </Typography>
                  <Typography variant="body1">
                    {selectedCliente.documentosAsociados} documento(s)
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Fecha de Registro
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedCliente.fechaRegistro)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    ID del Cliente
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {selectedCliente.id}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de documentos del cliente */}
      <Dialog open={documentosDialogOpen} onClose={() => setDocumentosDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Documentos de {selectedCliente?.nombre}
        </DialogTitle>
        <DialogContent>
          {loadingDocumentos ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {documentosCliente.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No hay documentos asociados"
                    secondary="Este cliente no tiene documentos registrados"
                  />
                </ListItem>
              ) : (
                documentosCliente.map((documento, index) => (
                  <React.Fragment key={documento.id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>
                          <Description />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={documento.titulo}
                        secondary={
                          <Box>
                            <Typography variant="body2">
                              Estado: {documento.estado}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Creado: {formatDate(documento.fechaCreacion)} | 
                              Expira: {formatDate(documento.fechaExpiracion)}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip
                        label={documento.estado}
                        color={getDocumentoEstadoColor(documento.estado) as any}
                        size="small"
                      />
                    </ListItem>
                    {index < documentosCliente.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentosDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListadoClientes; 